import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { addDoc, collection, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../contexts/authContext';
import Layout from './Layout';

const CATEGORIES = [
  'Bible Stories',
  'Relationships',
  'Life Questions',
  'Identity',
  'All about God',
  'Life Principles'
];

const CreateChildrenResource = () => {
  const { trackId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const [trackName, setTrackName] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [resource, setResource] = useState({
    category: '',
    title: '',
    subtitle: '',
    description: '',
    content: '',
    authorName: '',
    authorWebsite: '',
    imageURL: '',
    videoURL: '',
    videoTranscription: '',
    displayOrder: ''
  });

  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [videoPreview, setVideoPreview] = useState('');

  useEffect(() => {
    const loadTrackName = async () => {
      if (!trackId) return;
      try {
        const trackDoc = await getDoc(doc(db, 'contentTracks', trackId));
        if (trackDoc.exists()) {
          const trackData = trackDoc.data();
          setTrackName(trackData.name || 'Content Track');
        }
      } catch (error) {
        console.error('Error loading track name:', error);
      }
    };

    loadTrackName();
  }, [trackId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setResource(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate image file
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    setSelectedImage(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate video file
    if (!file.type.startsWith('video/')) {
      alert('Please select a video file');
      return;
    }

    setSelectedVideo(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setVideoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async (file) => {
    if (!file || !currentUser) {
      console.warn('uploadImage: Missing file or user', { file: !!file, user: !!currentUser });
      return null;
    }

    try {
      setUploadingMedia(true);
      const filename = `children-resources/${trackId}/${Date.now()}_${file.name}`;
      console.log('📤 Uploading image:', { filename, fileSize: file.size, fileType: file.type });
      const storageRef = ref(storage, filename);

      await uploadBytes(storageRef, file);
      console.log('✅ Image bytes uploaded successfully');
      
      const downloadURL = await getDownloadURL(storageRef);
      console.log('✅ Image download URL obtained:', downloadURL);
      return downloadURL;
    } catch (error) {
      console.error('❌ Error uploading image:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      alert(`Failed to upload image: ${error.message || 'Unknown error'}. Please try again.`);
      return null;
    } finally {
      setUploadingMedia(false);
    }
  };

  const uploadVideo = async (file) => {
    if (!file || !currentUser) return null;

    try {
      setUploadingMedia(true);
      const filename = `children-resources/${trackId}/videos/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, filename);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading video:', error);
      alert('Failed to upload video. Please try again.');
      return null;
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!trackId) return;

    // Prevent double submission - use submitted flag instead of loading
    if (loading || submitted) {
      console.warn('Form already submitted, ignoring duplicate submission');
      return;
    }

    // Validate required fields
    if (!resource.category) {
      alert('Please select a category');
      return;
    }

    if (!resource.title || !resource.title.trim()) {
      alert('Please enter a title');
      return;
    }

    if (!resource.description || !resource.description.trim()) {
      alert('Please enter a description');
      return;
    }

    if (!resource.content || !resource.content.trim()) {
      alert('Please enter article content');
      return;
    }

    setLoading(true);
    setSubmitted(true);

    try {
      // Upload image if selected
      let imageURL = resource.imageURL;
      if (selectedImage) {
        console.log('🖼️ Starting image upload...');
        const uploadedImageURL = await uploadImage(selectedImage);
        if (uploadedImageURL) {
          imageURL = uploadedImageURL;
          console.log('✅ Image upload complete, URL:', imageURL);
        } else {
          console.error('❌ Image upload failed, aborting submission');
          setLoading(false);
          setSubmitted(false);
          return;
        }
      } else {
        console.log('ℹ️ No image selected, using existing imageURL:', imageURL || 'none');
      }

      // Upload video if selected
      let videoURL = resource.videoURL;
      if (selectedVideo) {
        const uploadedVideoURL = await uploadVideo(selectedVideo);
        if (uploadedVideoURL) {
          videoURL = uploadedVideoURL;
        } else {
          setLoading(false);
          return;
        }
      }

      // Create article document in the articles collection for tmrsrc.com hosting
      // Include all fields needed for display on the Article page
      console.log('📝 Creating article document with imageURL:', imageURL || 'NONE PROVIDED');
      console.log('📝 Article data being saved:', {
        title: resource.title.trim(),
        category: resource.category,
        trackId: trackId,
        imageURL: imageURL || 'EMPTY',
        hasImage: !!imageURL && imageURL.length > 0
      });
      const articleData = {
        title: resource.title.trim(),
        content: resource.content.trim(), // Main article content
        description: resource.description.trim(),
        subtitle: resource.subtitle.trim() || '',
        imageURL: imageURL || '',
        image: imageURL || '', // Also store as 'image' for compatibility
        videoURL: videoURL || '',
        videoTranscription: resource.videoTranscription.trim() || '',
        author: resource.authorName.trim() || '', // Store as 'author' for compatibility
        authorName: resource.authorName.trim() || '',
        authorWebsite: resource.authorWebsite.trim() || '',
        website: resource.authorWebsite.trim() || '', // Also store as 'website' for compatibility
        category: resource.category,
        trackId: trackId,
        createdAt: serverTimestamp(),
        createdBy: {
          userId: currentUser?.uid || '',
          name: userData?.name || ''
        }
      };

      // Create the article document and get its ID
      console.log('💾 Saving article to Firestore with data:', articleData);
      const articleRef = await addDoc(collection(db, 'articles'), articleData);
      console.log('✅ Article document created with ID:', articleRef.id);
      console.log('✅ Article document includes imageURL:', articleData.imageURL || 'NONE');

      // Generate the tmrsrc.com URL using the article document ID
      const articleDomain = process.env.REACT_APP_ARTICLE_DOMAIN || 'https://tmrsrc.com';
      const articleUrl = `${articleDomain}/article/${articleRef.id}`;
      console.log('🔗 Generated article URL:', articleUrl);

      // Create the resource document in the track's resources subcollection
      const resourcesRef = collection(db, 'contentTracks', trackId, 'resources');
      const payload = {
        category: resource.category,
        title: resource.title.trim(),
        subtitle: resource.subtitle.trim() || '', // Image subtitle
        description: resource.description.trim(),
        content: resource.content.trim(), // Article content
        authorName: resource.authorName.trim() || '',
        authorWebsite: resource.authorWebsite.trim() || '',
        imageURL: imageURL || '',
        videoURL: videoURL || '',
        videoTranscription: resource.videoTranscription.trim() || '',
        type: videoURL ? 'video' : 'article',
        url: articleUrl, // The tmrsrc.com URL for viewing the resource
        status: 'published',
        createdAt: serverTimestamp(),
        createdBy: {
          userId: currentUser?.uid || '',
          name: userData?.name || ''
        }
      };

      if (resource.displayOrder) {
        const displayOrder = parseInt(resource.displayOrder, 10);
        if (!Number.isNaN(displayOrder)) {
          payload.displayOrder = displayOrder;
        }
      }

      await addDoc(resourcesRef, payload);
      
      // Navigate immediately after success
      navigate(`/content-tracks/${trackId}`);
    } catch (error) {
      console.error('Error creating children resource:', error);
      alert(`Failed to create resource: ${error.message || 'Unknown error'}. Please try again.`);
      setSubmitted(false); // Reset on error so user can try again
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-4">
        <h1 className="text-2xl font-bold mb-2">Create Children&apos;s Resource</h1>
        <p className="text-sm text-gray-600 mb-6">
          Track: {trackName}
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              name="category"
              value={resource.category}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md"
              required
            >
              <option value="">Select a category</option>
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={resource.title}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              required
              placeholder="Enter the title of the resource"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={resource.description}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              rows="3"
              required
              placeholder="Enter a brief description/summary of the resource"
            />
          </div>

          {/* Article Content */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">
              Article Content <span className="text-red-500">*</span>
            </label>
            <textarea
              name="content"
              value={resource.content}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              rows="12"
              required
              placeholder="Enter the full article content, story text, or lesson material here..."
            />
            <p className="text-sm text-gray-500 mt-1">
              This is the main content that will be displayed to children. Include the full story, lesson, or article text here.
            </p>
          </div>

          {/* Author Name */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">Author Name</label>
            <input
              type="text"
              name="authorName"
              value={resource.authorName}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              placeholder="Enter author name"
            />
          </div>

          {/* Author Website */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">Author Website</label>
            <input
              type="url"
              name="authorWebsite"
              value={resource.authorWebsite}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              placeholder="https://example.com"
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">Photo</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full p-2 border rounded"
            />
            {imagePreview && (
              <div className="mt-2">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-w-xs h-auto rounded border"
                />
              </div>
            )}
            {resource.imageURL && !selectedImage && (
              <div className="mt-2 text-sm text-gray-600">
                Current image URL: {resource.imageURL}
              </div>
            )}
            <input
              type="url"
              name="imageURL"
              value={resource.imageURL}
              onChange={handleChange}
              className="w-full p-2 border rounded mt-2"
              placeholder="Or enter image URL"
            />
          </div>

          {/* Image Subtitle */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">Image Subtitle</label>
            <input
              type="text"
              name="subtitle"
              value={resource.subtitle}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              placeholder="Text that appears beneath the image on the webpage"
            />
            <p className="text-sm text-gray-500 mt-1">
              This text will appear directly below the image when displayed on tmrsrc.com
            </p>
          </div>

          {/* Video Upload */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">Video</label>
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoChange}
              className="w-full p-2 border rounded"
            />
            {videoPreview && (
              <div className="mt-2">
                <video
                  src={videoPreview}
                  controls
                  className="max-w-xs rounded border"
                />
              </div>
            )}
            {resource.videoURL && !selectedVideo && (
              <div className="mt-2 text-sm text-gray-600">
                Current video URL: {resource.videoURL}
              </div>
            )}
            <input
              type="url"
              name="videoURL"
              value={resource.videoURL}
              onChange={handleChange}
              className="w-full p-2 border rounded mt-2"
              placeholder="Or enter video URL"
            />
          </div>

          {/* Video Transcription */}
          {resource.videoURL && (
            <div>
              <label className="block text-gray-700 font-bold mb-2">Video Transcription</label>
              <textarea
                name="videoTranscription"
                value={resource.videoTranscription}
                onChange={handleChange}
                className="w-full p-2 border rounded"
                rows="8"
                placeholder="Enter the full transcription of the video"
              />
            </div>
          )}

          {/* Display Order */}
          <div>
            <label className="block text-gray-700 font-bold mb-2">Display Order (optional)</label>
            <input
              type="number"
              name="displayOrder"
              value={resource.displayOrder}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              min="1"
              placeholder="Lower numbers appear first"
            />
          </div>

          <button
            type="submit"
            className="bg-[#0076BE] text-white px-6 py-2 rounded hover:bg-[#005c94] disabled:bg-gray-400 disabled:cursor-not-allowed"
            disabled={loading || uploadingMedia || submitted}
          >
            {loading || uploadingMedia || submitted
              ? (uploadingMedia ? 'Uploading...' : submitted ? 'Creating...' : 'Creating...')
              : 'Create Resource'}
          </button>
        </form>
      </div>
    </Layout>
  );
};

export default CreateChildrenResource;
